import * as Yup from 'yup';
import { Button, Dialog, DialogContent, Stack } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useNotify } from 'src/hooks/use-notify';
import useConfirm from 'src/basic-components/use-confirm';
import T from 'src/basic-components/text';
import { useApi } from 'src/contexts/api-provider';
import { DialogSection } from 'src/basic-components/dialog-section';
import { FormikTextView } from 'src/basic-components/formik-inputs';
import { LoadingButton } from '@mui/lab';
import { FormikCodeSelection } from 'src/basic-components/formik-inputs/formik-code-entry';

export const ManageUserBankAccountModal = ({ open, onClose, data }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const notify = useNotify();

  const [ConfirmationDialog, confirm] = useConfirm(
    undefined,
    <T title={data?.title}>are-you-sure-to-delete-record-@title</T>
  );

  const { CoreApi } = useApi();

  if (!open || !data) return <></>;
  const userId = data?.userId;
  const apiName = 'bankAccountsApi';
  const api = {
    saveByAdmin: CoreApi(apiName, 'saveByAdmin'),
  };

  if (!data) return '';

  const validationSchema = Yup.object({
    shebaNo: Yup.string().required(t('required')),
    cardNumber: Yup.string().required(t('required')),
    ownerName: Yup.string().required(t('required')),
  });

  return (
    <>
      <Dialog open={open} onClose={() => onClose()}>
        <Formik
          initialValues={{
            id: data.id,
            bankTitle: data.bankTitle || '',
            shebaNo: data.shebaNo || '',
            cardNumber: data.cardNumber || '',
            ownerName: data.ownerName || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setIsLoading(true);
            api
              .saveByAdmin(userId, values)
              .then((model) => {
                onClose(model.bankAccount);
                notify.success(t('changes-were-successfully-saved'));
              })
              .catch(notify.error)
              .finally(() => setIsLoading(false));
          }}
        >
          {(formik) => (
            <Form>
              <DialogSection title={data.id ? <T>edit</T> : <T>add</T>}>
                <DialogContent sx={{ p: 1 }}>
                  <Stack flex flexDirection="column" spacing={2}>
                    <FormikCodeSelection
                      focus
                      formik={formik}
                      width={64}
                      height={64}
                      count={4}
                      maxLength={4}
                      name="cardNumber"
                      label="card-number"
                      onCompleted={(value) => {
                        if (!value) return;
                        var bank = data.banks.find((bank) => bank.bin === value.substring(0, 6));
                        if (bank) formik.setFieldValue('bankTitle', bank.title);
                        formik.setFieldValue('cardNumber', value);
                      }}
                    />
                    <FormikTextView name="bankTitle" label="bank-title" formik={formik} />
                    <FormikTextView name="ownerName" label="owner-name" formik={formik} />
                    <FormikCodeSelection
                      width={44}
                      height={44}
                      formik={formik}
                      count={6}
                      maxLength={4}
                      name="shebaNo"
                      label="sheba-number"
                      onCompleted={(value) => {
                        if (!value) return;
                        formik.setFieldValue('shebaNo', value);
                      }}
                    />
                    <Stack flex justifyContent="end" flexDirection="row" gap={1} sx={{ width: '100%' }}>
                      <Button size="small" type="button" variant="outlined" onClick={() => onClose()}>
                        <T>cancel</T>
                      </Button>
                      <LoadingButton
                        size="small"
                        onClick={() => {
                          console.log(formik.errors);
                        }}
                        loading={isLoading}
                        type="submit"
                        variant="contained"
                      >
                        <T>apply</T>
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </DialogContent>
              </DialogSection>
            </Form>
          )}
        </Formik>
      </Dialog>
      <ConfirmationDialog />
    </>
  );
};
